export interface ILoginData {
  login: string;
  password: string;
}

export interface IMakeRequestConfig {
  method: string;
  url: string;
  headers: [string, string][];
  withCredentials?: boolean;
  body?: string;
}

export interface IMakeJSONPRequestConfig {
  url: string;
}

export interface ITask {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (): Promise<any>;
}

export interface IValidationInfo {
  logOnInfo: ILogOnInfo[];
  login: string;
  redirectBeforeAuthenticate: boolean;
  redirectUrl?: string;
  message?: string;
  errors?: ILoginError[];
}

export interface ILoginError {
  key: string;
  message: string;
  code: number | string | null;
}

export interface ILogOnInfo {
  logOnUrl: string;
  token: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function seriesP(tasks: ITask[]): Promise<any> {
  return tasks.reduce((acc, task) => {
    return acc.then(() => {
      return task();
    });
  }, Promise.resolve());
}

let jsonpCounter = 0;
export function makeJSONPRequest<T>(config: IMakeJSONPRequestConfig) {
  return new Promise((resolve, reject) => {
    const hash = `_jsonpCallback${jsonpCounter++}`;

    const script = document.createElement('script');
    script.async = true;
    script.src =
      config.url.indexOf('?') >= 0
        ? `${config.url}&_=${Math.random()}&callback=${hash}`
        : `${config.url}?_=${Math.random()}&callback=${hash}`;
    script.onerror = e => {
      reject(e);
    };

    window.addEventListener('error', handleWindowError);

    // Handle html responses, for example 302 redirect
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function handleWindowError(ev: Event) {
      window.removeEventListener('error', handleWindowError);
      reject();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)[hash] = (data: T) => {
      document.head.removeChild(script);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any)[hash];
      window.removeEventListener('error', handleWindowError);
      resolve(data);
    };

    document.head.appendChild(script);
  });
}

export function makeRequest(config: IMakeRequestConfig) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const url = config.url.indexOf('?') >= 0 ? `${config.url}&_=${Math.random()}` : `${config.url}?_=${Math.random()}`;

    xhr.open(config.method, url, true);

    if (typeof config.withCredentials !== 'undefined') {
      xhr.withCredentials = config.withCredentials;
    } else {
      xhr.withCredentials = true;
    }

    config.headers.forEach(header => {
      xhr.setRequestHeader(header[0], header[1]);
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          resolve(xhr.responseText);
        } catch (e) {
          reject(e);
        }
      }
    };

    xhr.onerror = e => {
      reject(e);
    };

    if (config.body) {
      xhr.send(config.body);
    } else {
      xhr.send();
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class Authenticator {
  public static logOut(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (res: any) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => void,
    host: string = '',
  ) {
    this.processLogout(host)
      .then(onSuccess)
      .catch(onError);
  }

  private static processLogout(host: string) {
    return makeRequest({
      method: 'GET',
      url: `${host}/api/users/logout-urls/`,
      headers: [],
      withCredentials: false,
    }).then((body: string) => {
      return Authenticator.processLinks(JSON.parse(body));
    });
  }

  private static processLinks(logoutLinks: string[]) {
    return seriesP(
      logoutLinks.map(logoutLink => {
        return () => Authenticator.performLogout(logoutLink);
      }),
    );
  }

  private static performLogout(logoutUrl: string) {
    return makeJSONPRequest({
      url: logoutUrl,
    });
  }
}
